
import { useEffect, useCallback, useRef, useState } from 'react'
import { useRouter } from 'next/router'
import Box from '@mui/material/Box'
import List from '@mui/material/List'
import MuiDialog from '@mui/material/Dialog'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import useMediaQuery from '@mui/material/useMediaQuery'
import { styled, useTheme } from '@mui/material/styles'
import ListItemButton from '@mui/material/ListItemButton'
import InputAdornment from '@mui/material/InputAdornment'
import CircularProgress from '@mui/material/CircularProgress'
import Icon from 'src/@core/components/icon'
import { getGlobalSearchResults } from 'src/configs/api'
import { useAPI } from 'src/utils/api'

const Dialog = styled(MuiDialog)({
  '& .MuiBackdrop-root': {
    backdropFilter: 'blur(4px)'
  }
})

const CategoryTitle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  marginBottom: theme.spacing(2),
  '& .category-icon-wrap': {
    width: 22,
    height: 22,
    borderRadius: '6px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0
  },
  '& .category-label': {
    fontSize: '13px',
    fontWeight: 700,
    textTransform: 'uppercase',
    letterSpacing: '0.8px',
    color: 'rgba(76, 78, 100, 0.87)',
    whiteSpace: 'nowrap'
  },
  '&::after': {
    content: '""',
    flex: 1,
    height: '1px',
    backgroundColor: '#e2e8f0'
  }
}))

const ResultItem = styled(ListItemButton)(({ theme }) => ({
  borderRadius: '4px !important',
  padding: theme.spacing(0.9, 1.5),
  marginBottom: theme.spacing(0.5),
  '&:hover': {
    backgroundColor: 'rgba(27, 32, 92, 0.05)',
    '& .result-chevron': { transform: 'translateX(3px)' },
    '& .result-label': {
      textDecoration: 'underline',
      textDecorationColor: '#1B205C'
    }
  }
}))

const NoResult = ({ value }) => {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', flexDirection: 'column', justifyContent: 'center' }}>
      <Box sx={{ mb: 2.5, color: 'text.primary' }}>
        <Icon icon='mdi:file-remove-outline' fontSize='5rem' />
      </Box>
      <Typography variant='h6' sx={{ mb: 11.5, wordWrap: 'break-word' }}>
        No results for{' '}
        <Typography variant='h6' component='span' sx={{ wordWrap: 'break-word' }}>
          {`"${value}"`}
        </Typography>
      </Typography>

      <Typography variant='body2' sx={{ mb: 2.5, color: 'text.disabled' }}>
        Try searching for something else
      </Typography>
    </Box>
  )
}

const AutocompleteComponent = ({ hidden, settings }) => {
  // ** States
  const [isMounted, setIsMounted] = useState(false)
  const [searchValue, setSearchValue] = useState('')
  const [openDialog, setOpenDialog] = useState(false)
  const [, setOptions] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [groupedResults, setGroupedResults] = useState({})

  const theme = useTheme()
  const router = useRouter()
  const { layout } = settings
  const wrapper = useRef(null)
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down('sm'))
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'))
  const api = useAPI()
  const categoryInfo = {
    segments: {
      title: 'Segments',
      icon: 'mdi:filter-variant',
      iconColor: '#e67e22',
      iconBgColor: '#fef3e7',
      route: '/customer-data-platform/segment/'
    },
    promotional: {
      title: 'Promotional Campaigns',
      icon: 'mdi:bullhorn',
      iconColor: '#e74c3c',
      iconBgColor: '#fadbd8',
      route: '/engagement/promotional-campaign/campaign/'
    },
    transactional: {
      title: 'Transactional Campaigns',
      icon: 'mdi:email-fast',
      iconColor: '#27ae60',
      iconBgColor: '#eafaf1',
      route: '/engagement/transactional-campaign/campaign/'
    },
    templates: {
      title: 'Templates',
      icon: 'mdi:file-document-multiple',
      iconColor: '#8e44ad',
      iconBgColor: '#f4ecf7',
      route: '/content/templates/'
    },
    custom_reports: {
      title: 'Custom Reports',
      icon: 'mdi:file-chart',
      iconColor: '#9b59b6',
      iconBgColor: '#f4ecf7',
      route: '/custom-report'
    },
    journeys: {
      title: 'Marketing Automation',
      icon: 'mdi:robot',
      iconColor: '#f39c12',
      iconBgColor: '#fef5e7',
      route: '/engagement/marketing-automation/journeys/'
    },
    journey_builder: {
      title: 'Journey Builder',
      icon: 'mdi:robot',
      iconColor: '#f39c12',
      iconBgColor: '#fef5e7',
      route: '/engagement/marketing-automation/journeys/'
    },
    other: {
      title: 'Other',
      icon: 'mdi:tag-outline',
      iconColor: '#64748b',
      iconBgColor: '#f1f5f9',
      route: '/'
    }
  }

  const fetchSearchResults = async (query) => {
    if (query.length < 3) {
      setOptions([])
      setGroupedResults({})
      return
    }
    setIsLoading(true)

    try {
      const url = `${getGlobalSearchResults}?query=${encodeURIComponent(query)}`

      const response = await api.get(url
        
      )

      if (response?.data?.Data && Array.isArray(response.data.Data)) {
        const formattedOptions = response?.data?.Data.map(item => ({
          id: item.search_id,
          title: item.search_name,
          tags: item.search_tags,
          type: item.search_type || 'other',
          url: `/${item.search_type}/${item.search_id}`
        }))

        setOptions(formattedOptions)
        const grouped = {}
        formattedOptions.forEach(item => {
          const type = item.type.toLowerCase()
          if (!grouped[type]) {
            grouped[type] = []
          }
          grouped[type].push(item)
        })

        setGroupedResults(grouped)
      } else {
        setOptions([])
        setGroupedResults({})
      }
    } catch {
      setOptions([])
      setGroupedResults({})
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (searchValue.length >= 3) {
      setIsLoading(true)
    }
    const handler = setTimeout(() => {
      if (searchValue.length >= 3) {
        fetchSearchResults(searchValue)
      } else {
        setOptions([])
        setGroupedResults({})
        setIsLoading(false)
      }
    }, 300)

    return () => {
      clearTimeout(handler)
    }
  }, [searchValue])

  useEffect(() => {
    if (!openDialog) {
      setSearchValue('')
      setOptions([])
      setGroupedResults({})
    }
  }, [openDialog])

  useEffect(() => {
    setIsMounted(true)

    return () => setIsMounted(false)
  }, [])

  const handleOptionClick = obj => {
    setSearchValue('')
    setOpenDialog(false)

    if (obj.type) {
      const type = obj.type.toLowerCase()
      switch (type) {
        case 'segments':
          router.push({
            pathname: '/customer-data-platform/segment/',
            query: { segmentId: obj.id, action: 'edit' }
          })
          break
        case 'promotional':
          router.push({
            pathname: '/engagement/promotional-campaign/campaign/',
            query: { campaignId: obj.id, action: 'edit' }
          })
          break
        case 'transactional':
          router.push({
            pathname: '/engagement/transactional-campaign/campaign/',
            query: { campaignId: obj.id, action: 'edit' }
          })
          break
        case 'templates':
          router.push({
            pathname: '/content/templates/',
            query: { templateId: obj.id, action: 'edit' }
          })
          break
        case 'custom_reports':
          router.push({
            pathname: '/custom-report',
            query: { reportId: obj.id, action: 'edit' }
          })
          break
        case 'journey_builder':
          router.push({
            pathname: '/engagement/marketing-automation/journeys/',
            query: { journeyBuilderId: obj.id, action: 'edit' }
          })
          break
        case 'journeys':
          router.push({
            pathname: '/engagement/marketing-automation/journeys/',
            query: { journeyId: obj.id, action: 'edit' }
          })
          break
        default:
          if (obj.url) {
            router.push('/home')
          }
          break
      }
    } else if (obj.url) {
      router.push('/home')
    }
  }

  const handleKeydown = useCallback(
    event => {
      if (!openDialog && event.ctrlKey && event.which === 191) {
        setOpenDialog(true)
      }
    },
    [openDialog]
  )

  const handleKeyUp = useCallback(
    event => {
      if (openDialog && event.keyCode === 27) {
        setOpenDialog(false)
      }
    },
    [openDialog]
  )

  useEffect(() => {
    document.addEventListener('keydown', handleKeydown)
    document.addEventListener('keyup', handleKeyUp)

    return () => {
      document.removeEventListener('keydown', handleKeydown)
      document.removeEventListener('keyup', handleKeyUp)
    }
  }, [handleKeyUp, handleKeydown])

  const getCategoryInfo = (type) => {
    const lowerType = type?.toLowerCase() || 'other'
    return categoryInfo[lowerType] || categoryInfo.other
  }

  const renderResultsGrid = () => {
    const categories = Object.keys(categoryInfo)

    return (
      <Box sx={{ px: 5, py: 3 }}>
        {categories.map(category => {
          const items = groupedResults[category] || []
          const info = categoryInfo[category]

          if (items.length === 0) return null

          return (
            <Box key={category} sx={{ mb: 5 }}>
              <CategoryTitle>
                <span className='category-icon-wrap' style={{ backgroundColor: info.iconColor }}>
                  <Icon icon={info.icon} fontSize={13} style={{ color: 'white' }} />
                </span>
                <span className='category-label'>{info.title}</span>
              </CategoryTitle>
              <List disablePadding sx={{ pl: '20px' }}>
                {items.map(item => (
                  <ResultItem key={item.id} onClick={() => handleOptionClick(item)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <Icon icon='mdi:chevron-right' fontSize={14} className='result-chevron' style={{ color: info.iconColor, flexShrink: 0, transition: 'transform 0.2s ease' }} />
                      <Typography className='result-label' variant='body2' noWrap title={item.title}
                        sx={{ fontWeight: 600, color: '#1B205C', fontSize: '13px' }}>
                        {item.title || 'Unnamed Item'}
                      </Typography>
                    </Box>
                  </ResultItem>
                ))}
              </List>
            </Box>
          )
        })}
      </Box>
    )
  }

  const renderCompactResults = () => {
    return (
      <Box sx={{ px: 3, py: 2 }}>
        {Object.keys(groupedResults).map(category => {
          const items = groupedResults[category] || []
          const info = getCategoryInfo(category)

          if (items.length === 0) return null

          return (
            <Box key={category} sx={{ mb: 5 }}>
              <CategoryTitle>
                <span className='category-icon-wrap' style={{ backgroundColor: info.iconColor }}>
                  <Icon icon={info.icon} fontSize={13} style={{ color: 'white' }} />
                </span>
                <span className='category-label'>{info.title}</span>
              </CategoryTitle>
              <List disablePadding sx={{ pl: '34px' }}>
                {items.map(item => (
                  <ResultItem key={item.id} onClick={() => handleOptionClick(item)}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                      <Icon icon='mdi:chevron-right' fontSize={14} className='result-chevron' style={{ color: info.iconColor, flexShrink: 0, transition: 'transform 0.2s ease' }} />
                      <Typography className='result-label' variant='body2' noWrap title={item.title}
                        sx={{ fontWeight: 600, color: '#1B205C', fontSize: '13px' }}>
                        {item.title || 'Unnamed Item'}
                      </Typography>
                    </Box>
                  </ResultItem>
                ))}
              </List>
            </Box>
          )
        })}
      </Box>
    )
  }

  const renderSearchResults = () => {
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 6 }}>
          <CircularProgress size={28} sx={{ color: '#1B205C' }} />
        </Box>
      )
    } else if (searchValue.length < 3) {
      return (
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography>Type at least 3 characters to search</Typography>
        </Box>
      )
    } else if (Object.keys(groupedResults).length === 0) {
      return <NoResult value={searchValue} setOpenDialog={setOpenDialog} />
    } else {
      return isSmUp ? renderResultsGrid() : renderCompactResults()
    }
  }

  if (!isMounted) {
    return null
  } else {
    return (
      <Box
        ref={wrapper}
        onClick={() => !openDialog && setOpenDialog(true)}
        sx={{ display: 'flex', cursor: 'pointer', alignItems: 'center', mr: '0.7rem' }}
      >

        <IconButton color='inherit' sx={!hidden && layout === 'vertical' ? { mr: 1, ml: -2.75 } : {}}>
          <Icon icon='mdi:magnify' />
        </IconButton>
        {!hidden && layout === 'vertical' ? (
          <Typography sx={{ userSelect: 'none', color: 'text.disabled' }}>Search (Ctrl+/)</Typography>
        ) : null}
        {openDialog && (
          <Dialog
            disableRestoreFocus
            maxWidth='sm'
            fullWidth
            open={openDialog}
            fullScreen={fullScreenDialog}
            onClose={() => setOpenDialog(false)}
            PaperProps={{
              sx: { borderRadius: '16px', overflow: 'hidden' }
            }}
          >
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              {/* CXPify dialog header */}
              <Box sx={{ backgroundColor: '#1B205C', p: 4, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                  <Box>
                    <Typography variant='h6' sx={{ color: 'white', fontWeight: 600, lineHeight: 1.3 }}>Global Search</Typography>
                    <Typography variant='caption' sx={{ color: 'rgba(255,255,255,0.6)' }}>Search across segments, campaigns, templates and more</Typography>
                  </Box>
                </Box>
                <IconButton
                  onClick={() => setOpenDialog(false)}
                  sx={{ color: 'white', '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' } }}
                >
                  <Icon icon='mdi:close' />
                </IconButton>
              </Box>

              {/* Search input */}
              <Box sx={{ px: 4, py: 3, flexShrink: 0 }}>
                <TextField
                  fullWidth
                  value={searchValue}
                  onChange={event => setSearchValue(event.target.value)}
                  placeholder='Search by name or tags...'
                  inputRef={input => {
                    if (input && openDialog) {
                      input.focus()
                    }
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start' sx={{ color: 'text.primary' }}>
                        <Icon icon='mdi:magnify' />
                      </InputAdornment>
                    ),
                    endAdornment: searchValue ? (
                      <InputAdornment
                        position='end'
                        onClick={() => setSearchValue('')}
                        sx={{ display: 'flex', cursor: 'pointer', alignItems: 'center' }}
                      >
                        <IconButton size='small' sx={{ p: 1 }}>
                          <Icon icon='mdi:close' fontSize={20} />
                        </IconButton>
                      </InputAdornment>
                    ) : null
                  }}
                />
              </Box>

              {/* Results */}
              <Box sx={{ overflow: 'auto', minHeight: 300, maxHeight: 400 }}>
                {renderSearchResults()}
              </Box>
            </Box>
          </Dialog>
        )}
      </Box>
    )
  }
}

export default AutocompleteComponent